import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaWanx26I2vParams {
  /**
   * 输入参数
   */
  input?: {
    /**
     * 视频生成提示词，最大长度2000字符
     * @length 0 - 2000
     */
    prompt?: string;
    /**
     * 输入图片URL或Base64编码（自动转换为JPEG格式的Base64）
     */
    img_url: string;
    /**
     * 自定义背景音乐URL（可选）
     */
    audio_url?: string;
  };
  /**
   * 生成参数
   */
  parameters?: {
    /**
     * 视频分辨率，可选值：720P、1080P
     * @default "1080P"
     * @values 720P | 1080P
     */
    resolution?: '720P' | '1080P';
    /**
     * 视频时长（秒），可选值：5、10、15
     * @default 5
     * @values 5 | 10 | 15
     */
    duration?: 5 | 10 | 15;
    /**
     * 是否启用提示词扩展
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     * @default false
     */
    watermark?: boolean;
    /**
     * 是否生成音频
     * @default true
     */
    audio?: boolean;
    /**
     * 是否启用多镜头模式（仅在prompt_extend=true时生效）
     */
    multi_shots?: boolean;
    /**
     * 反向提示词，描述不希望出现的内容
     */
    negative_prompt?: string;
    /**
     * 随机种子，用于复现生成结果
     */
    seed?: number;
  };
}

export interface AlibabaWanx26I2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx26_i2v
 *
 * <p>API 文档: https://help.aliyun.com/zh/model-studio/image-to-video-api-reference</p>
 * <p>Alibaba Wanx2.6 I2V 图生视频生成接口，支持将图片转换为视频，支持多种分辨率和时长选项。</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx26I2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx26I2v({
 *   input: {
 *     img_url: "https://example.com/image.jpg",
 *     prompt: "A beautiful video animation with smooth movements",
 *     audio_url: "https://example.com/music.mp3"
 *   },
 *   parameters: {
 *     resolution: "1080P",
 *     duration: 10,
 *     prompt_extend: true,
 *     audio: true,
 *     multi_shots: true,
 *     watermark: false
 *   }
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx26I2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx26I2v(
  params: AlibabaWanx26I2vParams,
): Promise<AlibabaWanx26I2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx26_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx26I2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
